import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Copy, HardDrive, Search } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardHeader } from '@/components/ui/Card';
import { Button, ButtonLink } from '@/components/ui/Button';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/states/EmptyState';
import { ErrorState } from '@/components/states/ErrorState';
import { DeviceStateBadge } from '@/components/domain/DeviceStateBadge';
import { OnlineDot } from '@/components/domain/OnlineDot';
import { RelativeTime } from '@/components/domain/RelativeTime';
import { listDevicesAdmin } from '@/api/endpoints/devices';
import { queryKeys } from '@/api/queryKeys';
import { cn } from '@/lib/cn';
import { DeviceActionDialog, type DeviceAction } from './DeviceActionDialog';
import type { DeviceAdmin, DeviceState } from '@/types/api';

const STATES: readonly DeviceState[] = ['unclaimed', 'claimed', 'revoked', 'locked', 'rma'];

/**
 * Which lifecycle actions make sense from each state. This mirrors the server's
 * state machine rather than guessing: `rma` is terminal and offers nothing, and
 * restore only accepts a revoked or locked device.
 */
const ACTIONS: Record<DeviceState, readonly DeviceAction[]> = {
  unclaimed: ['revoke'],
  claimed: ['revoke', 'rma'],
  revoked: ['restore', 'rma'],
  locked: ['restore'],
  rma: [],
};

const ACTION_LABELS: Record<DeviceAction, string> = {
  revoke: 'Revoke',
  rma: 'RMA',
  restore: 'Restore',
};

/** Restoring a locked device releases its lockout; it does not change ownership. */
function actionLabel(action: DeviceAction, state: DeviceState): string {
  if (action === 'restore' && state === 'locked') return 'Unlock';
  return ACTION_LABELS[action];
}

/**
 * Provisioning defaults a unit's serial number to its device_uid, so both are a
 * 26-character opaque string. Revoking asks the administrator to type that serial
 * out; without a way to copy it, that confirmation is unusable rather than merely
 * careful.
 */
function CopyableId({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <button
      type="button"
      aria-label={`Copy ${value}`}
      title="Copy"
      onClick={() => {
        navigator.clipboard
          .writeText(value)
          .then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          })
          .catch(() => undefined); // Clipboard blocked; the value is on screen to read.
      }}
      className="group inline-flex items-center gap-1.5 text-left font-mono text-[12px] text-ink-secondary hover:text-ink"
    >
      {value}
      {copied ? (
        <Check className="size-3.5 shrink-0 text-ok" aria-hidden />
      ) : (
        <Copy className="size-3.5 shrink-0 text-ink-muted opacity-0 transition-opacity group-hover:opacity-100" aria-hidden />
      )}
    </button>
  );
}

export function AdminDevicesPage() {
  const [state, setState] = useState<DeviceState | ''>('');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [pending, setPending] = useState<{ action: DeviceAction; device: DeviceAdmin } | null>(null);

  // Typing a serial number should not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const devices = useQuery({
    queryKey: queryKeys.devicesAdmin(state, query),
    queryFn: () => listDevicesAdmin({ state: state || undefined, q: query || undefined }),
  });

  const rows = useMemo(() => devices.data ?? [], [devices.data]);

  // Counts come from the rows already loaded rather than a second endpoint. They
  // therefore describe the current filter, which is why the strip is hidden while
  // one is applied — a "3 claimed" next to a filtered list would be a lie.
  const counts = useMemo(() => {
    const tally = Object.fromEntries(STATES.map((s) => [s, 0])) as Record<DeviceState, number>;
    for (const device of rows) tally[device.state] += 1;
    return tally;
  }, [rows]);

  const unfiltered = state === '' && query === '';

  return (
    <>
      <PageHeader
        title="Device registry"
        description="Every provisioned device. Identity is minted here and burned into the unit at manufacture; the server accepts telemetry only from a device that can prove it holds the matching secret."
        action={
          <ButtonLink to="/admin/provisioning" variant="primary">
            Provision devices
          </ButtonLink>
        }
      />

      {unfiltered && rows.length > 0 && (
        <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
          {STATES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setState(s)}
              className="card px-3 py-2.5 text-left transition-colors hover:bg-surface-sunken"
            >
              <span className="block text-[20px] font-semibold text-ink" data-numeric>
                {counts[s]}
              </span>
              <span className="mt-1 block">
                <DeviceStateBadge state={s} />
              </span>
            </button>
          ))}
        </div>
      )}

      <Card>
        <CardHeader
          title="All devices"
          description="Newest first."
          action={
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-ink-muted"
                  aria-hidden
                />
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Device ID or serial"
                  aria-label="Search devices"
                  className="h-9 w-52 rounded-[--radius-control] border border-line bg-surface pl-8 pr-3 text-[13px] outline-none focus:border-accent focus:ring-2 focus:ring-accent-ring/40"
                />
              </div>
              <select
                value={state}
                onChange={(event) => setState(event.target.value as DeviceState | '')}
                aria-label="Filter by state"
                className="h-9 rounded-[--radius-control] border border-line bg-surface px-2 text-[13px] outline-none focus:border-accent focus:ring-2 focus:ring-accent-ring/40"
              >
                <option value="">All states</option>
                {STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          }
        />

        {devices.isPending ? (
          <SkeletonRows rows={6} />
        ) : devices.isError ? (
          <ErrorState error={devices.error} onRetry={devices.refetch} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={HardDrive}
            title={unfiltered ? 'No devices yet' : 'Nothing matches'}
            description={
              unfiltered
                ? 'Provision a batch to mint device identities for the flashing bench.'
                : 'No device matches that state or search.'
            }
            {...(unfiltered
              ? {
                  action: (
                    <ButtonLink to="/admin/provisioning" variant="primary">
                      Provision devices
                    </ButtonLink>
                  ),
                }
              : {})}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="border-b border-line text-left text-ink-muted">
                <tr>
                  <th className="px-4 py-2 font-medium">State</th>
                  <th className="px-4 py-2 font-medium">Device ID</th>
                  <th className="hidden px-4 py-2 font-medium sm:table-cell">Serial</th>
                  <th className="hidden px-4 py-2 font-medium md:table-cell">Owner</th>
                  <th className="hidden px-4 py-2 font-medium lg:table-cell">Batch</th>
                  <th className="px-4 py-2 font-medium">Last seen</th>
                  <th className="px-4 py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {rows.map((device) => (
                  <tr
                    key={device.id}
                    className={cn(
                      'transition-colors hover:bg-surface-sunken',
                      device.state === 'revoked' && 'bg-bad-soft/30',
                      device.state === 'locked' && 'bg-warn-soft/40'
                    )}
                  >
                    <td className="px-4 py-2.5">
                      <DeviceStateBadge state={device.state} />
                    </td>
                    <td className="px-4 py-2.5">
                      {device.device_uid ? <CopyableId value={device.device_uid} /> : '—'}
                    </td>
                    <td className="hidden px-4 py-2.5 sm:table-cell">{device.serial_number}</td>
                    <td className="hidden px-4 py-2.5 text-ink-secondary md:table-cell">
                      {device.owner_name ? (
                        <span title={device.owner_email ?? undefined}>{device.owner_name}</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="hidden px-4 py-2.5 text-ink-secondary lg:table-cell">
                      {device.hw_batch ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5">
                      <span className="flex items-center gap-2">
                        <OnlineDot online={device.is_online} />
                        <span className="text-ink-muted">
                          <RelativeTime value={device.last_seen} />
                        </span>
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="flex justify-end gap-1.5">
                        {ACTIONS[device.state].length === 0 ? (
                          <span className="text-ink-muted">—</span>
                        ) : (
                          ACTIONS[device.state].map((action) => (
                            <Button
                              key={action}
                              size="sm"
                              variant={action === 'restore' ? 'secondary' : 'ghost'}
                              // device_uid is what every lifecycle route takes. A row
                              // without one predates the identity migration.
                              disabled={!device.device_uid}
                              onClick={() => setPending({ action, device })}
                            >
                              {actionLabel(action, device.state)}
                            </Button>
                          ))
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {pending && (
        <DeviceActionDialog
          action={pending.action}
          device={pending.device}
          onClose={() => setPending(null)}
        />
      )}
    </>
  );
}
